import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Personal TO-DO notebook state — one private list per agent.
 *
 * Ported almost verbatim from the design prototype's `Component` logic (see the
 * design handoff). The only production changes vs. the prototype:
 *   • Data is keyed PER USER (`wq_todos_<agentId>`) so no agent ever sees
 *     another's list. Widget-position keys stay device-local (handled in the
 *     component, not here).
 *   • No demo/seed data — a fresh agent starts empty.
 *
 * Persistence is localStorage (device-local). To later sync lists across an
 * agent's devices, swap the load/save here for a small `/api/todos` resource —
 * the rest of the component is storage-agnostic.
 */

export interface SubTask {
  id: string
  text: string
  done: boolean
}

export interface Todo {
  id: string
  text: string
  done: boolean
  subtasks: SubTask[]
  note: string
  /** Only set on a Pending task: the day (toDateString) it rolled over from. */
  from?: string
}

export type ListKey = 'today' | 'tomorrow' | 'week' | 'pending'

export interface TodoData {
  today: Todo[]
  tomorrow: Todo[]
  week: Todo[]
  pending: Todo[]
  /** Day stamp (Date.toDateString) used to detect a rollover. */
  _day?: string
}

const LIST_KEYS: ListKey[] = ['today', 'tomorrow', 'week', 'pending']

function emptyData(): TodoData {
  return { today: [], tomorrow: [], week: [], pending: [] }
}

function storageKey(userId: string): string {
  return `wq_todos_${userId}`
}

/** Load + normalise + roll over the stored list for a given user. */
function loadForUser(userId: string): TodoData {
  const todayKey = new Date().toDateString()
  let t: TodoData | null = null
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (raw) t = JSON.parse(raw)
  } catch {
    /* corrupt / unavailable — start fresh */
  }
  if (!t || !t.today) t = emptyData()
  if (!t.pending) t.pending = []
  // Normalise every task so subtasks/note always exist.
  for (const k of LIST_KEYS) {
    if (!Array.isArray(t[k])) t[k] = []
    for (const x of t[k]) {
      if (!x.subtasks) x.subtasks = []
      if (x.note === undefined) x.note = ''
    }
  }
  // Day rollover: incomplete Today tasks from a previous day → Pending
  // (stamped with the day they came from); completed ones stay in Today.
  const lastDay = t._day
  if (lastDay && lastDay !== todayKey) {
    const stale = t.today.filter((x) => !x.done)
    t.pending = [...t.pending, ...stale.map((x) => ({ ...x, from: lastDay }))]
    t.today = t.today.filter((x) => x.done)
  }
  t._day = todayKey
  return t
}

const newId = (p: string) => p + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)

/**
 * Add a task to an agent's TO-DO notebook from OUTSIDE the notebook (e.g. the
 * cancelled-booking follow-up button). Writes storage directly and pings the
 * mounted notebook to reload, so the task appears immediately.
 */
export function addTodoToNotebook(userId: string, text: string) {
  const trimmed = text.trim()
  if (!userId || !trimmed) return
  const t = loadForUser(userId)
  t.today.push({ id: newId('x'), text: trimmed, done: false, subtasks: [], note: '' })
  try { localStorage.setItem(storageKey(userId), JSON.stringify(t)) } catch { /* ignore */ }
  try { window.dispatchEvent(new Event('wq-todos-external')) } catch { /* ignore */ }
}

/**
 * Add a follow-up to-do for a cancelled booking ONCE per booking per device
 * (a marker key prevents duplicates on re-render/refresh). Used by the automatic
 * cancellation follow-up — the lead-side task is created by the server; this is
 * the personal-notebook half.
 */
export function addFollowUpTodoOnce(userId: string, bookingId: string, text: string) {
  if (!userId || !bookingId) return
  const marker = `wq_fu_${userId}_${bookingId}`
  try {
    if (localStorage.getItem(marker)) return
    localStorage.setItem(marker, '1')
  } catch { return }
  addTodoToNotebook(userId, text)
}

export interface UseTodos {
  todos: TodoData
  findTask: (id: string) => { task: Todo; key: ListKey } | null
  addTask: (tab: ListKey, text: string) => void
  toggleTask: (id: string) => void
  removeTask: (id: string) => void
  moveTask: (id: string, toKey: ListKey) => void
  /** Live-reorder within one list (used by the grip drag). */
  reorder: (tab: ListKey, fromIndex: number, toIndex: number) => void
  addSubtask: (taskId: string, text: string) => void
  toggleSubtask: (taskId: string, subId: string) => void
  removeSubtask: (taskId: string, subId: string) => void
  setNote: (taskId: string, note: string) => void
}

export function useTodos(userId: string | null): UseTodos {
  const [todos, setTodos] = useState<TodoData>(emptyData)
  const userRef = useRef<string | null>(null)

  // (Re)load whenever the authenticated user changes. Runs the rollover once.
  useEffect(() => {
    userRef.current = userId
    if (!userId) {
      setTodos(emptyData())
      return
    }
    const loaded = loadForUser(userId)
    setTodos(loaded)
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(loaded))
    } catch {
      /* ignore */
    }
    // External writers (addTodoToNotebook) ping this event so the open notebook
    // shows the new task without a reload.
    const onExternal = () => { if (userRef.current) setTodos(loadForUser(userRef.current)) }
    window.addEventListener('wq-todos-external', onExternal)
    return () => window.removeEventListener('wq-todos-external', onExternal)
  }, [userId])

  /** Immutable update: clone → mutate → persist → set. */
  const update = useCallback((fn: (draft: TodoData) => void) => {
    setTodos((prev) => {
      const next: TodoData = JSON.parse(JSON.stringify(prev))
      fn(next)
      const uid = userRef.current
      if (uid) {
        try {
          localStorage.setItem(storageKey(uid), JSON.stringify(next))
        } catch {
          /* ignore */
        }
      }
      return next
    })
  }, [])

  const findInDraft = (draft: TodoData, id: string): Todo | null => {
    for (const k of LIST_KEYS) {
      const it = draft[k].find((x) => x.id === id)
      if (it) return it
    }
    return null
  }

  const findTask = useCallback<UseTodos['findTask']>(
    (id) => {
      for (const k of LIST_KEYS) {
        const task = todos[k].find((x) => x.id === id)
        if (task) return { task, key: k }
      }
      return null
    },
    [todos]
  )

  const addTask = useCallback<UseTodos['addTask']>(
    (tab, text) => {
      const trimmed = text.trim()
      if (!trimmed || tab === 'pending') return
      update((d) => {
        d[tab].push({ id: newId('x'), text: trimmed, done: false, subtasks: [], note: '' })
      })
    },
    [update]
  )

  const toggleTask = useCallback<UseTodos['toggleTask']>(
    (id) => update((d) => { const it = findInDraft(d, id); if (it) it.done = !it.done }),
    [update]
  )

  const removeTask = useCallback<UseTodos['removeTask']>(
    (id) => update((d) => { for (const k of LIST_KEYS) d[k] = d[k].filter((x) => x.id !== id) }),
    [update]
  )

  const moveTask = useCallback<UseTodos['moveTask']>(
    (id, toKey) => update((d) => {
      let moved: Todo | null = null
      for (const k of LIST_KEYS) {
        const idx = d[k].findIndex((x) => x.id === id)
        if (idx !== -1) { moved = d[k].splice(idx, 1)[0]; break }
      }
      if (!moved) return
      delete moved.from // moving out of Pending clears the rollover stamp
      d[toKey].push(moved)
    }),
    [update]
  )

  const reorder = useCallback<UseTodos['reorder']>(
    (tab, fromIndex, toIndex) => update((d) => {
      const arr = d[tab]
      if (fromIndex < 0 || fromIndex >= arr.length || toIndex < 0 || toIndex >= arr.length) return
      const [it] = arr.splice(fromIndex, 1)
      arr.splice(toIndex, 0, it)
    }),
    [update]
  )

  const addSubtask = useCallback<UseTodos['addSubtask']>(
    (taskId, text) => {
      const trimmed = text.trim()
      if (!trimmed) return
      update((d) => { const it = findInDraft(d, taskId); if (it) it.subtasks.push({ id: newId('s'), text: trimmed, done: false }) })
    },
    [update]
  )

  const toggleSubtask = useCallback<UseTodos['toggleSubtask']>(
    (taskId, subId) => update((d) => {
      const it = findInDraft(d, taskId)
      const st = it?.subtasks.find((y) => y.id === subId)
      if (st) st.done = !st.done
    }),
    [update]
  )

  const removeSubtask = useCallback<UseTodos['removeSubtask']>(
    (taskId, subId) => update((d) => {
      const it = findInDraft(d, taskId)
      if (it) it.subtasks = it.subtasks.filter((y) => y.id !== subId)
    }),
    [update]
  )

  const setNote = useCallback<UseTodos['setNote']>(
    (taskId, note) => update((d) => { const it = findInDraft(d, taskId); if (it) it.note = note }),
    [update]
  )

  return { todos, findTask, addTask, toggleTask, removeTask, moveTask, reorder, addSubtask, toggleSubtask, removeSubtask, setNote }
}
